import { useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { StaticFooter } from '@/components/static-footer';
import { StaticNav } from '@/components/static-nav';
import { SignInButton } from '@/components/sign-in-button';
import { Button } from '@/components/ui/button';
import { signIn } from '@/lib/cloudflare/auth-client';
import { buildGettingStartedPath } from '@/lib/cloudflare/billing-client';
import { IconFilter, IconArrowsUpDown, IconBrain, IconTarget, IconLock, IconCheck, IconSparkles, IconBolt, IconPointer } from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { usePostHog } from 'posthog-js/react';
import * as Sentry from '@sentry/react';
import arrowWithScribble from '@/assets/annotations/arrow-with-scribble-svgrepo-com.svg';
import rightUpArrow from '@/assets/annotations/right-up-arrow-svgrepo-com.svg';
import turnRightArrow from '@/assets/annotations/turn-right-arrow-svgrepo-com.svg';

const mockEmails: Record<string, Array<{ from: string; subj: string; snippet: string; tag: string; time: string; unread?: boolean; starred?: boolean; priority?: 'important' | 'low' }>> = {
  Inbox: [
    { from: 'Acme', subj: 'Need revised proposal before noon', snippet: 'Customer asked for pricing confirmation before lunch.', tag: 'Work', time: '8m ago', unread: true, priority: 'important' },
    { from: 'Legal', subj: 'Contract attached for signature', snippet: 'Attached PDF is ready for final review and signature.', tag: 'Finance', time: '22m ago', unread: true, priority: 'important' },
    { from: 'Candidate', subj: 'Candidate sent final availability', snippet: 'Needs a decision before the team sync tomorrow.', tag: 'Work', time: '41m ago', unread: true, priority: 'important' },
    { from: 'Amazon', subj: 'Your package has been delivered', snippet: 'Your order #112-1234567-8901234 was delivered today.', tag: 'Shopping', time: '52m ago', unread: true },
    { from: 'Notion', subj: 'Workspace update: 3 new features', snippet: 'We have released new features including AI summaries and better sync.', tag: 'Updates', time: '2h ago', unread: true },
    { from: 'GitHub', subj: 'Your pull request was merged', snippet: 'PR #234: Fix authentication bug in user login flow has been merged.', tag: 'Work', time: '3h ago' },
    { from: 'Stripe', subj: 'Your invoice is ready', snippet: 'Invoice #INV-2024-1234 for $450.00 is ready for review.', tag: 'Finance', time: '4h ago', unread: true },
    { from: 'LinkedIn', subj: '5 people viewed your profile', snippet: 'See who viewed your profile this week and how they found you.', tag: 'Social', time: '5h ago' },
  ],
  Unsubscribe: [
    { from: 'Groupon', subj: 'Deal of the Day: 50% Off Spa Day', snippet: 'Treat yourself to a relaxing spa experience. Limited time offer.', tag: 'Marketing', time: '1h ago' },
    { from: 'Gap', subj: 'Extra 40% Off Sale Items', snippet: 'Take an extra 40% off all sale items. Plus free shipping.', tag: 'Marketing', time: '3h ago' },
    { from: 'Zappos', subj: 'Free Shipping on Everything', snippet: 'No minimum purchase required. Free shipping on all orders.', tag: 'Shopping', time: '5h ago' },
    { from: 'Target', subj: 'Your weekly deals are here', snippet: 'This week only: Extra 20% off clearance items. Shop now!', tag: 'Marketing', time: '1d ago' },
    { from: 'Best Buy', subj: 'Flash Sale: 40% Off Laptops', snippet: 'Limited time offer on select laptops. While supplies last.', tag: 'Marketing', time: '2d ago' },
    { from: 'Sephora', subj: 'Beauty Insider: 20% Off Everything', snippet: 'Your exclusive member discount is here. Valid through Sunday.', tag: 'Marketing', time: '3d ago' },
    { from: 'Wayfair', subj: 'Spring sale: Up to 60% Off', snippet: 'Refresh your home this season with our biggest sale.', tag: 'Marketing', time: '4d ago' },
    { from: 'REI', subj: 'Member exclusive: Extra 20% Off', snippet: 'As a member, you get an extra 20% off already reduced items.', tag: 'Marketing', time: '5d ago' },
  ],
  Archive: [
    { from: 'NYT', subj: "Today's Top Stories", snippet: 'Breaking: Tech stocks rally after earnings reports.', tag: 'Newsletter', time: '4d ago' },
    { from: 'Uber', subj: 'Your trip receipt from yesterday', snippet: 'Thanks for riding with Uber. Your trip cost $12.50.', tag: 'Receipt', time: '1d ago' },
    { from: 'Apple', subj: 'Your receipt from the App Store', snippet: 'Thank you for your purchase. Here is your digital receipt.', tag: 'Receipt', time: '2d ago' },
    { from: 'Netflix', subj: 'Your monthly statement is ready', snippet: 'Your Netflix statement for January is now available.', tag: 'Receipt', time: '3d ago' },
    { from: 'The Verge', subj: 'Weekly digest: Top 10 stories', snippet: 'The most important tech news from the past week.', tag: 'Newsletter', time: '4d ago' },
    { from: 'GitHub', subj: 'Weekly digest: Your repositories', snippet: '12 commits, 3 PRs, and 5 issues were updated.', tag: 'Updates', time: '5d ago' },
    { from: 'Spotify', subj: 'Your 2024 listening is ready', snippet: 'See your top artists, songs, and podcasts from this year.', tag: 'Newsletter', time: '1w ago' },
    { from: 'Marriott', subj: 'Your stay summary is ready', snippet: 'Summary of your recent stay at Marriott Downtown is available.', tag: 'Travel', time: '2w ago' },
  ],
  Delete: [
    { from: 'Sales Genius', subj: 'Quick question about your Q1 goals?', snippet: 'I was looking at your website and noticed a few things...', tag: 'Spam', time: '1d ago' },
    { from: 'Webinar Host', subj: 'Last chance to register for today!', snippet: "Don't miss out on our expert panel discussing the latest trends.", tag: 'Spam', time: '2d ago' },
    { from: 'Crypto Alert', subj: 'URGENT: Your account has been compromised', snippet: 'Click here to secure your funds immediately.', tag: 'Spam', time: '5d ago', priority: 'low' },
    { from: 'Free Money', subj: 'You have won $1,000,000!', snippet: 'Congratulations! Click here to claim your prize now!', tag: 'Spam', time: '1w ago', priority: 'low' },
    { from: 'Nigerian Prince', subj: 'URGENT: Need your help', snippet: 'I have $50 million that I need to transfer out of the country.', tag: 'Spam', time: '2w ago', priority: 'low' },
    { from: 'Weight Loss', subj: 'Lose 30 lbs in 30 days!', snippet: 'Doctor-approved weight loss program. Limited spots available.', tag: 'Spam', time: '3w ago', priority: 'low' },
    { from: 'Casino', subj: 'FREE SPINS - No deposit required', snippet: 'Claim your 50 free spins now. New members only!', tag: 'Spam', time: '1mo ago', priority: 'low' },
    { from: 'Tech Support', subj: 'Your computer may be infected', snippet: 'Call immediately: 1-800-555-0199 to fix your PC.', tag: 'Spam', time: '1mo ago', priority: 'low' },
  ],
};

const tagColors: Record<string, { bg: string; text: string }> = {
  'Important': { bg: '#fef3c7', text: '#92400e' },
  'Shopping': { bg: '#fce7f3', text: '#9d174d' },
  'Finance': { bg: '#fef3c7', text: '#92400e' },
  'Work': { bg: '#dbeafe', text: '#1e40af' },
  'Marketing': { bg: '#e9d5ff', text: '#6b21a8' },
  'Newsletter': { bg: '#dbeafe', text: '#1e40af' },
  'Receipt': { bg: '#dcfce7', text: '#166534' },
  'Spam': { bg: '#f3f4f6', text: '#4b5563' },
};

function EmailCard({
  email,
  proofTargetRef,
  proofTagRef,
}: {
  email: typeof mockEmails.Inbox[0];
  proofTargetRef?: RefObject<HTMLDivElement | null>;
  proofTagRef?: RefObject<HTMLSpanElement | null>;
}) {
  const tagStyle = tagColors[email.tag] || { bg: '#f3f4f6', text: '#4b5563' };
  const isUnread = email.unread;

  return (
    <div className={cn(
      "px-2 py-1.5 border-b border-border/40 transition-colors hover:bg-muted/30"
    )} ref={proofTargetRef}>
      {/* Row 1: Star + Unread dot + Sender + Priority + Time */}
      <div className="flex items-center gap-1.5 mb-0.5">
        {/* Star */}
        <span className={cn(
          "shrink-0",
          email.starred ? "text-yellow-500" : "text-slate-300"
        )}>
          <svg className="h-3 w-3" viewBox="0 0 24 24" fill={email.starred ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        </span>
        
        {/* Unread dot */}
        {isUnread && <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />}
        
        {/* Sender */}
        <span className={cn(
          "truncate text-[11px] flex-1",
          isUnread ? "font-semibold text-foreground" : "text-muted-foreground"
        )}>
          {email.from}
        </span>

        {/* Priority */}
        {email.priority === 'important' && (
          <span className="shrink-0 rounded bg-amber-500/15 px-1 py-0.5 text-[8px] font-semibold text-amber-600">!</span>
        )}
        {email.priority === 'low' && (
          <span className="shrink-0 rounded bg-gray-500/15 px-1 py-0.5 text-[8px] font-medium text-gray-500">↓</span>
        )}

        {/* Time */}
        <span className="text-[9px] text-muted-foreground shrink-0">{email.time}</span>
      </div>

      {/* Row 2: Subject */}
      <h3 className={cn(
        "text-[11px] truncate mb-0.5",
        isUnread ? "font-medium text-foreground" : "text-foreground/70"
      )}>
        {email.subj}
      </h3>

      {/* Row 3: Snippet + Label */}
      <div className="flex items-center justify-between gap-1">
        <p className="text-[10px] text-muted-foreground truncate flex-1">{email.snippet}</p>
        <span
          ref={proofTagRef}
          className="shrink-0 rounded px-1.5 py-0.5 text-[9px] font-medium"
          style={{ backgroundColor: tagStyle.bg, color: tagStyle.text }}
        >
          {email.tag}
        </span>
      </div>
    </div>
  );
}

function ProductMockup() {
  const mockupRef = useRef<HTMLDivElement | null>(null);
  const proofTargetRef = useRef<HTMLDivElement | null>(null);
  const unsubscribeActionRef = useRef<HTMLSpanElement | null>(null);
  const labelTagRef = useRef<HTMLSpanElement | null>(null);
  const [replyPosition, setReplyPosition] = useState<{ left: number; top: number } | null>(null);
  const [cleanupPosition, setCleanupPosition] = useState<{ left: number; top: number } | null>(null);
  const [labelPosition, setLabelPosition] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const clamp = (value: number, min: number, max: number) =>
      Math.min(Math.max(value, min), max);

    const updateProofPosition = () => {
      if (!mockupRef.current) return;

      const mockupRect = mockupRef.current.getBoundingClientRect();

      if (proofTargetRef.current) {
        const targetRect = proofTargetRef.current.getBoundingClientRect();
        setReplyPosition({
          left: targetRect.left - mockupRect.left,
          top: targetRect.top - mockupRect.top + (targetRect.height / 2),
        });
      }

      if (unsubscribeActionRef.current) {
        const actionRect = unsubscribeActionRef.current.getBoundingClientRect();
        setCleanupPosition({
          left: actionRect.left - mockupRect.left + (actionRect.width / 2),
          top: actionRect.top - mockupRect.top - 5,
        });
      }

      if (labelTagRef.current) {
        const labelRect = labelTagRef.current.getBoundingClientRect();
        setLabelPosition({
          left: labelRect.left - mockupRect.left + labelRect.width,
          top: labelRect.top - mockupRect.top + (labelRect.height / 2),
        });
      }
    };

    updateProofPosition();
    window.addEventListener('resize', updateProofPosition);

    return () => window.removeEventListener('resize', updateProofPosition);
  }, []);

  const columns = [
    { name: 'Inbox', count: 8, action: null },
    { name: 'Unsubscribe', count: 8, action: 'Unsubscribe All' },
    { name: 'Archive', count: 8, action: 'Archive All' },
    { name: 'Delete', count: 8, action: 'Delete All' },
  ];

  return (
    <div ref={mockupRef} className="relative w-full max-w-6xl mx-auto overflow-visible">
      {/* Browser frame */}
      <div className="relative overflow-visible">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-200/50 overflow-visible">
          <div className="overflow-hidden rounded-[inherit]">
            {/* Browser chrome */}
            <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2.5">
              <div className="flex gap-1.5">
                <div className="h-3 w-3 rounded-full bg-[#ff5f57]" />
                <div className="h-3 w-3 rounded-full bg-[#ffbd2e]" />
                <div className="h-3 w-3 rounded-full bg-[#28ca42]" />
              </div>
            </div>

            {/* Kanban columns */}
            <div className="flex bg-muted/30 overflow-x-auto snap-x snap-mandatory">
              {columns.map((col) => (
                <div
                  key={col.name}
                  className="min-w-[260px] md:min-w-[220px] flex-1 border-r border-border/40 last:border-r-0 bg-white flex flex-col snap-center"
                >
                  {/* Column header */}
                  <div className="px-3 py-2 border-b border-border/40 flex items-center justify-between bg-slate-50 shrink-0">
                    <span className="text-[13px] font-medium text-muted-foreground">
                      {col.name} <span className="text-[11px] text-slate-400">({col.count})</span>
                    </span>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1 text-slate-300">
                        <IconFilter className="h-3.5 w-3.5" />
                        <IconArrowsUpDown className="h-3.5 w-3.5" />
                      </div>
                      {col.action && (
                        <span
                          ref={col.name === 'Unsubscribe' ? unsubscribeActionRef : undefined}
                          className="text-[10px] font-medium text-[#0A84FF] cursor-pointer hover:text-[#0875e1]"
                        >
                          {col.action}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Email list */}
                  <div
                    className={cn(
                      "flex-1",
                      col.name === 'Inbox' ? "overflow-visible" : "overflow-hidden"
                    )}
                  >
                    {(mockEmails[col.name as keyof typeof mockEmails] || []).map((email, i) => (
                      <EmailCard
                        key={i}
                        email={email}
                        proofTargetRef={col.name === 'Inbox' && i === 0 ? proofTargetRef : undefined}
                        proofTagRef={col.name === 'Delete' && i === 0 ? labelTagRef : undefined}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {replyPosition && (
        <div
          className="pointer-events-none absolute hidden lg:block z-50"
          style={{ left: `${replyPosition.left}px`, top: `${replyPosition.top}px` }}
        >
          <div className="relative w-0 h-0">
            <span
              className="absolute right-[60px] top-[-20px] rotate-[-4deg] whitespace-nowrap text-[28px] leading-none text-[#4285F4]"
              style={{ fontFamily: "'Caveat', cursive" }}
            >
              reply this first
            </span>

            <img
              src={turnRightArrow}
              alt=""
              aria-hidden="true"
              className="absolute right-[20px] top-[-15px] h-[35px] w-auto max-w-none rotate-[15deg] opacity-95"
            />
          </div>
        </div>
      )}

      {cleanupPosition && (
        <div
          className="pointer-events-none absolute hidden md:block z-50"
          style={{ left: `${cleanupPosition.left}px`, top: `${cleanupPosition.top}px` }}
        >
          <div className="relative w-0 h-0">
            <span
              className="absolute right-[-40px] top-[-60px] rotate-[-2deg] whitespace-nowrap text-[27px] leading-none text-[#EA4335]"
              style={{ fontFamily: "'Caveat', cursive" }}
            >
              unsubscribe all in one click
            </span>

            <img
              src={arrowWithScribble}
              alt=""
              aria-hidden="true"
              className="absolute left-[-10px] top-[-40px] h-[40px] w-auto max-w-none rotate-[100deg] opacity-95"
            />
          </div>
        </div>
      )}

      {labelPosition && (
        <div
          className="pointer-events-none absolute hidden lg:block z-50"
          style={{ left: `${labelPosition.left}px`, top: `${labelPosition.top}px` }}
        >
          <div className="relative w-0 h-0">
            <span
              className="absolute left-[40px] top-[-20px] rotate-[2deg] whitespace-nowrap text-[27px] leading-none text-[#34A853]"
              style={{ fontFamily: "'Caveat', cursive" }}
            >
              AI sorted + labeled
            </span>

            <img
              src={rightUpArrow}
              alt=""
              aria-hidden="true"
              className="absolute left-[10px] top-[-10px] h-[35px] w-auto max-w-none -scale-x-100 rotate-[-20deg] opacity-95"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function LandingPage() {
  const posthog = usePostHog();
  const handleSignIn = () => {
    Sentry.metrics.count('button_click', 1, { attributes: { location: 'hero' } });
    if (posthog) posthog.capture('clicked_get_started', { location: 'hero' });
    signIn(buildGettingStartedPath());
  };

  return (
    <div className="min-h-screen bg-[#fafafa] text-slate-950">
      <StaticNav />

      {/* Hero */}
      {/* Hero */}
      <section className="min-h-[calc(100vh-3.5rem)] px-6 pt-32 pb-12">
        <div className="mx-auto max-w-5xl">
          {/* Text */}
          <div className="text-center mb-12">
            <h1 className="text-6xl font-semibold leading-[1.15] text-slate-950 sm:text-7xl font-['Switzer',_sans-serif] tracking-[-0.01em]">
              Stop dreading your inbox.
            </h1>
            <p className="mt-8 text-lg leading-relaxed text-slate-600 max-w-lg mx-auto">
              Email shouldn't be your morning job. Winbox Zero learns your habits to surface what actually matters and instantly clear the noise. Ten minutes to inbox zero. It just works.
            </p>
            <div className="mt-10 flex flex-col items-center gap-5">
              <button
                onClick={handleSignIn}
                className="inline-flex items-center gap-3 h-12 px-8 rounded-full border border-slate-200 bg-white text-base font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Connect Gmail to get started
              </button>

              {/* Risk-reversal strip */}
              <div className="inline-flex flex-wrap justify-center items-center gap-x-5 gap-y-2 text-sm text-slate-500">
                <span className="flex items-center gap-1.5">
                  <svg className="h-4 w-4 text-emerald-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  See the magic first. Try it before you pay.
                </span>
                <span className="flex items-center gap-1.5">
                  <svg className="h-4 w-4 text-emerald-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Absolute control. Read-only by default.
                </span>
                <span className="flex items-center gap-1.5">
                  <svg className="h-4 w-4 text-emerald-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Don't love it? Revoke access in one click.
                </span>
              </div>
            </div>
          </div>

          {/* Product mockup */}
          <div className="mt-20">
            <ProductMockup />
          </div>
        </div>
      </section>

      {/* Smart Rules & Automations */}
      <section className="border-t border-slate-200 bg-white px-6 py-32">
        <div className="mx-auto max-w-5xl">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <div>
              <h2 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                Automate your inbox with plain English.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-slate-600">
                Stop wrestling with complex filter builders. Just tell Winbox Zero what you want, and our LLM sets up the exact rules behind the scenes.
              </p>
              <ul className="mt-8 space-y-4">
                <li className="flex items-start gap-3">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                    <IconCheck className="w-3.5 h-3.5" />
                  </div>
                  <p className="text-slate-700">"If email is from Uber, label it Travel"</p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
                    <IconCheck className="w-3.5 h-3.5" />
                  </div>
                  <p className="text-slate-700">"Delete all cold outreach and newsletters"</p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600">
                    <IconCheck className="w-3.5 h-3.5" />
                  </div>
                  <p className="text-slate-700">"Emails from @amazon.com go to Shopping"</p>
                </li>
              </ul>
            </div>
            
                                    <div className="relative rounded-2xl border border-slate-200 bg-slate-50 p-8 shadow-sm">
              <div className="space-y-4">
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">New Rule</p>
                  <p className="text-slate-900 font-medium">Archive emails with PDF attachments</p>
                </div>
                
                <div className="flex justify-center py-2">
                  <svg className="h-6 w-6 text-slate-300 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
                  <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-green-500" />
                    <span className="text-xs font-medium text-slate-600">LLM Parsed Result</span>
                  </div>
                  <div className="p-4 space-y-3 font-mono text-sm">
                    <div className="flex gap-2">
                      <span className="text-purple-600 font-semibold">IF</span>
                      <span className="text-blue-600">attachment.type</span>
                      <span className="text-slate-500">==</span>
                      <span className="text-amber-600">'application/pdf'</span>
                    </div>
                    <div className="flex gap-2 border-t border-slate-200 pt-3 mt-3">
                      <span className="text-purple-600 font-semibold">THEN</span>
                      <span className="text-emerald-600">archive()</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="border-t border-slate-200 bg-[#fafafa] px-6 py-32 overflow-hidden">
        <div className="mx-auto max-w-5xl">
          <div className="grid gap-16 lg:grid-cols-2 items-center">
            <div className="relative h-[400px] w-full rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden flex flex-col">
              <div className="h-[220px] bg-slate-50 border-b border-slate-200 relative overflow-hidden flex items-center justify-center">
                <div className="absolute inset-0 opacity-30">
                  <div className="w-full h-full bg-[radial-gradient(#94a3b8_1px,transparent_1px)] [background-size:12px_12px]" />
                </div>

                <div className="absolute top-6 left-6 bg-white px-2.5 py-1.5 rounded-lg shadow-sm text-[10px] font-mono text-slate-500 transform -rotate-6 border border-slate-200 flex flex-col gap-1">
                  <span className="text-slate-400">{"{"}</span>
                  <span className="pl-3"><span className="text-blue-500">"action"</span>: <span className="text-emerald-500">"delete_unread"</span>,</span>
                  <span className="pl-3"><span className="text-blue-500">"count"</span>: <span className="text-amber-500">1402</span></span>
                  <span className="text-slate-400">{"}"}</span>
                </div>

                <div className="absolute top-12 right-6 bg-white px-2.5 py-1.5 rounded-lg shadow-sm text-[10px] font-mono text-slate-500 transform rotate-3 border border-slate-200 flex flex-col gap-1">
                  <span className="text-slate-400">{"{"}</span>
                  <span className="pl-3"><span className="text-blue-500">"sender"</span>: <span className="text-emerald-500">"@github.com"</span>,</span>
                  <span className="pl-3"><span className="text-blue-500">"reply_time"</span>: <span className="text-emerald-500">"never"</span></span>
                  <span className="text-slate-400">{"}"}</span>
                </div>

                <div className="absolute bottom-6 left-1/4 bg-white px-2.5 py-1.5 rounded-lg shadow-sm text-[10px] font-mono text-slate-500 transform rotate-6 border border-slate-200 opacity-90">
                  <span><span className="text-purple-500">label_applied</span>: "receipts"</span>
                </div>

                <div className="relative z-10 bg-white/95 backdrop-blur-md border border-slate-200 shadow-xl rounded-2xl px-8 py-5 text-center transform hover:scale-105 transition-transform duration-500">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Scanning History</p>
                  </div>
                  <p className="text-4xl font-bold text-slate-900 font-mono tracking-tight">142,853</p>
                  <p className="text-xs font-medium text-slate-400 mt-1">emails analyzed</p>
                </div>
              </div>

              <div className="flex-1 bg-white p-6 flex flex-col justify-center gap-4">
                <div className="flex items-start gap-4 p-3 rounded-xl bg-slate-50 border border-slate-100 hover:border-blue-200 hover:bg-blue-50/50 transition-colors">
                  <div className="bg-white border border-slate-200 p-2 rounded-lg text-blue-600 shadow-sm shrink-0">
                    <IconBrain className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900 mb-1">You ignore 84% of newsletters.</p>
                    <p className="text-xs text-slate-500 leading-relaxed">Model updated to automatically route promotional mail directly to the Unsubscribe queue.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-3 rounded-xl bg-slate-50 border border-slate-100 hover:border-emerald-200 hover:bg-emerald-50/50 transition-colors">
                  <div className="bg-white border border-slate-200 p-2 rounded-lg text-emerald-600 shadow-sm shrink-0">
                    <IconTarget className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900 mb-1">You reply to your team instantly.</p>
                    <p className="text-xs text-slate-500 leading-relaxed">Model updated to pin senders from @yourcompany.com to the top of your inbox as VIPs.</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                Your inbox has years of data.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-slate-600">
                The newsletters you never open. The vendors you always reply to. The receipts you keep and the promo emails you delete without reading.
              </p>
              <p className="mt-4 text-lg font-medium text-slate-900">
                You've been training a model for years. We just made it useful.
              </p>
              
              <div className="mt-10 flex flex-col gap-4">
                <div className="flex items-center gap-4 rounded-xl bg-white p-4 shadow-sm border border-slate-100">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-50 text-slate-600 font-bold">
                    1
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Pattern Recognition</p>
                    <p className="text-sm text-slate-500">We notice what you instantly delete.</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 rounded-xl bg-white p-4 shadow-sm border border-slate-100">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-50 text-slate-600 font-bold">
                    2
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Priority Detection</p>
                    <p className="text-sm text-slate-500">We highlight the senders you always reply to.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* How it works */}
      <section className="border-t border-slate-200 bg-white px-6 py-32">
        <div className="mx-auto max-w-5xl">
          <div className="text-center mb-20">
            <h2 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
              From chaos to clarity in 3 steps.
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              No complex setup or endless configurations. We do the heavy lifting.
            </p>
          </div>

          <div className="relative">
            <div className="grid gap-8 lg:grid-cols-3 relative z-10">
              
              {/* Step 1 */}
              <div className="relative flex flex-col rounded-3xl bg-white ring-1 ring-slate-200 shadow-sm overflow-hidden">
                <div className="h-48 bg-slate-50 border-b border-slate-100 p-4 relative flex flex-col justify-center items-center overflow-hidden">
                  <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:16px_16px] opacity-30" />
                  
                  <div className="relative z-10 w-full max-w-[200px] space-y-2 transform transition-transform hover:scale-105">
                    <div className="bg-white rounded border border-emerald-200 shadow-sm p-2 flex items-center justify-between">
                      <span className="text-[10px] font-mono text-slate-500">Headers & Sender</span>
                      <IconCheck className="w-3 h-3 text-emerald-500" />
                    </div>
                    <div className="bg-white rounded border border-slate-200 shadow-sm p-2 flex items-center justify-between opacity-50 grayscale relative overflow-hidden">
                      <span className="text-[10px] font-mono text-slate-400 blur-[1px]">Email Body Content...</span>
                      <IconLock className="w-3 h-3 text-slate-400 relative z-10" />
                      <div className="absolute inset-0 bg-[repeating-linear-gradient(45deg,transparent,transparent_4px,rgba(0,0,0,0.05)_4px,rgba(0,0,0,0.05)_8px)]" />
                    </div>
                  </div>
                  <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-blue-400/10 rounded-full blur-2xl pointer-events-none" />
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-600 font-bold text-sm">1</div>
                    <h3 className="text-xl font-semibold text-slate-950">Connect Gmail</h3>
                  </div>
                  <p className="leading-relaxed text-slate-600 text-sm">
                    Takes 30 seconds. We securely sync your metadata—sender, subject, and timestamps. <strong className="text-slate-900 font-medium">We never read your email content.</strong>
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative flex flex-col rounded-3xl bg-white ring-1 ring-slate-200 shadow-sm overflow-hidden">
                <div className="h-48 bg-slate-950 border-b border-slate-800 p-4 relative flex flex-col justify-center items-center overflow-hidden">
                  <div className="w-full max-w-[220px] bg-slate-900 rounded-lg border border-slate-800 p-3 font-mono text-[10px] text-emerald-400 space-y-1.5 shadow-2xl relative z-10 transform transition-transform hover:scale-105">
                     <p className="flex items-center gap-2"><span className="animate-pulse text-purple-400">▶</span> <span>Analyzing history...</span></p>
                     <p className="text-slate-500">  └─ 42,019 threads scanned</p>
                     <p className="flex items-center gap-2 mt-2"><span className="text-blue-400">✓</span> <span>VIP patterns mapped</span></p>
                     <p className="flex items-center gap-2"><span className="text-blue-400">✓</span> <span className="text-white font-medium">Model localized</span></p>
                  </div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl pointer-events-none" />
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100 text-purple-600 font-bold text-sm">2</div>
                    <h3 className="text-xl font-semibold text-slate-950">We Learn</h3>
                  </div>
                  <p className="leading-relaxed text-slate-600 text-sm">
                    Our LLM analyzes your history. Your labels, your stars, your reply patterns. We build a personalized model of exactly how you handle email.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative flex flex-col rounded-3xl bg-white ring-1 ring-slate-200 shadow-sm overflow-hidden">
                <div className="h-48 bg-emerald-50 border-b border-emerald-100 p-4 relative flex flex-col justify-center items-center overflow-hidden">
                  <div className="relative z-10 w-full max-w-[180px] bg-white rounded-xl shadow-lg border border-slate-100 p-4 text-center transform transition-transform hover:scale-105">
                     <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3">
                       <IconSparkles className="w-5 h-5" />
                     </div>
                     <p className="text-sm font-bold text-slate-900">Morning Briefing</p>
                     <div className="mt-3 space-y-1 text-left border-t border-slate-100 pt-3">
                       <p className="text-[10px] text-slate-500 flex justify-between"><span>Unsubscribed</span> <span className="font-bold text-slate-900">24</span></p>
                       <p className="text-[10px] text-slate-500 flex justify-between"><span>Archived</span> <span className="font-bold text-slate-900">102</span></p>
                       <p className="text-[10px] text-emerald-600 flex justify-between font-medium pt-1 mt-1"><span>Need Reply</span> <span className="font-bold">3</span></p>
                     </div>
                  </div>
                  <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-emerald-200/40 rounded-full blur-2xl pointer-events-none" />
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 font-bold text-sm">3</div>
                    <h3 className="text-xl font-semibold text-slate-950">Inbox Handled</h3>
                  </div>
                  <p className="leading-relaxed text-slate-600 text-sm">
                    Expired codes auto-archive. Unread newsletters surface for mass cleanup. The rest stays out of your way. Your inbox, finally quiet.
                  </p>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="border-t border-slate-200 bg-[#fafafa] px-6 py-32">
        <div className="mx-auto max-w-5xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
              Local-first principles. Cloud-scale power.
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              We built Winbox Zero with a paranoid approach to privacy. Your email is your business.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            <div className="rounded-3xl bg-white p-8 shadow-sm ring-1 ring-slate-200 md:col-span-2 flex flex-col justify-between">
              <div>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600 mb-6">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-slate-950">Zero Content Storage</h3>
                <p className="mt-3 text-slate-600 leading-relaxed text-lg">
                  We only analyze metadata (headers, senders, timestamps) and the 150-character preview snippet. <strong className="text-slate-900 font-medium">The actual body of your emails never enters our database, and never goes to our AI models.</strong>
                </p>
              </div>
            </div>

            <div className="rounded-3xl bg-white p-8 shadow-sm ring-1 ring-slate-200 flex flex-col justify-between">
              <div>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-50 text-green-600 mb-6">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-slate-950">Read-Only Safety</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">
                  We don't auto-delete anything. Every action requires your explicit approval. Gmail remains your absolute source of truth.
                </p>
              </div>
            </div>

            <div className="rounded-3xl bg-white p-8 shadow-sm ring-1 ring-slate-200 flex flex-col justify-between">
              <div>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-50 text-red-500 mb-6">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-slate-950">Instant Amnesia</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">
                  If you delete your account, we wipe everything instantly. No retention periods. No hidden backups.
                </p>
              </div>
            </div>

            <div className="rounded-3xl bg-slate-950 p-8 shadow-sm ring-1 ring-slate-900 md:col-span-2 flex flex-col justify-between overflow-hidden relative">
              <div className="relative z-10">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 text-white mb-6">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-white">Learns from corrections</h3>
                <p className="mt-3 text-slate-400 leading-relaxed text-lg max-w-xl">
                  Our model is tailored specifically to you. If it miscategorizes an email, just drag it to the correct column. <span className="text-slate-200 font-medium">We register the correction locally and never make that mistake again.</span>
                </p>
              </div>
              
              {/* Decorative background element */}
              <div className="absolute right-0 bottom-0 opacity-[0.03] pointer-events-none transform translate-x-1/4 translate-y-1/4">
                <svg className="w-64 h-64 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="border-t border-slate-200 bg-white px-6 py-32">
        <div className="mx-auto max-w-5xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
              Inbox zero is finally a default state.
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Join the operators and founders who took their mornings back.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-3xl bg-slate-50 p-8 ring-1 ring-slate-200 md:col-span-2 lg:col-span-2 flex flex-col justify-between">
              <div>
                <div className="flex text-amber-400 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="h-5 w-5 fill-current" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-xl md:text-2xl font-medium leading-relaxed text-slate-900 mb-8">
                  "I stared at it for 5 minutes after connecting. It had found 47 newsletters I'd been meaning to unsubscribe from for years. <span className="text-blue-600">One click and my inbox was completely silent.</span>"
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center text-blue-700 font-semibold text-lg">SC</div>
                <div>
                  <p className="text-base font-semibold text-slate-950">Sarah Chen</p>
                  <p className="text-sm text-slate-500">Founder, Lattice AI</p>
                </div>
              </div>
            </div>

            <div className="rounded-3xl bg-white p-8 shadow-sm ring-1 ring-slate-200 flex flex-col justify-between">
              <div>
                <p className="text-lg leading-relaxed text-slate-700 mb-8">
                  "Expected to spend hours training it. Instead it just... worked. It already knew which vendors I actually care about."
                </p>
              </div>
              <div className="flex items-center gap-4 border-t border-slate-100 pt-6">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center text-green-700 font-semibold">MR</div>
                <div>
                  <p className="text-sm font-semibold text-slate-950">Marcus Rodriguez</p>
                  <p className="text-xs text-slate-500">CEO, Pallet</p>
                </div>
              </div>
            </div>

            <div className="rounded-3xl bg-slate-950 p-8 shadow-sm ring-1 ring-slate-900 md:col-span-2 lg:col-span-3 flex flex-col justify-between overflow-hidden relative">
              <div className="relative z-10 flex flex-col md:flex-row gap-8 items-center md:items-start justify-between">
                <p className="text-xl md:text-2xl font-medium leading-relaxed text-white max-w-3xl">
                  "The anxiety I used to feel opening my inbox? Gone. <span className="text-slate-400">It's like having an executive assistant who read my mind and cleared my desk before I woke up.</span>"
                </p>
                <div className="flex items-center gap-4 shrink-0 bg-white/10 rounded-2xl p-4 backdrop-blur-sm">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center text-purple-700 font-semibold text-lg">JP</div>
                  <div>
                    <p className="text-base font-semibold text-white">Jamie Park</p>
                    <p className="text-sm text-slate-300">Operator, Rippling</p>
                  </div>
                </div>
              </div>
              
              <div className="absolute right-0 bottom-0 opacity-[0.05] pointer-events-none transform translate-x-1/4 translate-y-1/4">
                <svg className="w-64 h-64 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21L16.41 14c.223-.743.83-1.35 1.573-1.573L25 10l-7.017-2.427c-.743-.223-1.35-.83-1.573-1.573L14 0l-2.427 7.017c-.223.743-.83 1.35-1.573 1.573L3 10l7.017 2.427c.743.223 1.35.83 1.573 1.573L14 21z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-slate-200 bg-white px-6 py-32">
        <div className="mx-auto max-w-5xl">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-slate-50 border border-slate-200 px-8 py-20 shadow-sm sm:px-16 sm:py-24 text-center">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] rounded-full bg-blue-100/50 blur-3xl pointer-events-none" />
            
            <div className="relative z-10">
              <h2 className="mx-auto max-w-2xl text-4xl font-bold text-slate-900 sm:text-5xl font-['Switzer',_sans-serif] tracking-[-0.01em]">
                Ready to stop dreading your inbox?
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-slate-600">
                Join the operators who let AI do the heavy lifting. Get through your morning email in 10 minutes or less.
              </p>
              <div className="mt-10 flex flex-col items-center gap-4">
                <SignInButton location="hero" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <StaticFooter />
    </div>
  );
}
